/**
 * GridNavigator implementations for different grid types and topologies
 * Requirements: 2.4-2.11
 */

import type { Position, Direction } from './types.js';
import { GridType, Topology } from './types.js';
import type { IGridNavigator, MoveResult } from './interfaces.js';

/**
 * Количество направлений для каждого типа сетки
 * Req 2.8: треугольная - 3 направления
 * Req 2.9: квадратная - 4 направления
 * Req 2.10: шестиугольная - 6 направлений
 */
export function getDirectionCount(gridType: GridType): number {
  switch (gridType) {
    case GridType.Triangular:
      return 3;
    case GridType.Square:
      return 4;
    case GridType.Hexagonal:
      return 6;
  }
}

/**
 * Базовый класс для навигаторов квадратной сетки
 */
abstract class SquareGridNavigator implements IGridNavigator {
  protected readonly width: number;
  protected readonly height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  getDirectionCount(): number {
    return 4;
  }

  turn(currentDir: Direction, turnAmount: Direction, _pos?: Position): Direction {
    return (currentDir + turnAmount) % 4;
  }

  /**
   * Получить смещение для направления (квадратная сетка)
   * 0 = вверх (y-1), 1 = вправо (x+1), 2 = вниз (y+1), 3 = влево (x-1)
   */
  protected getOffset(dir: Direction): Position {
    const offsets: Position[] = [
      { x: 0, y: -1 },  // 0: вверх
      { x: 1, y: 0 },   // 1: вправо
      { x: 0, y: 1 },   // 2: вниз
      { x: -1, y: 0 }   // 3: влево
    ];
    return offsets[dir];
  }

  abstract move(pos: Position, dir: Direction): MoveResult;
}

/**
 * Навигатор для квадратной сетки с топологией тора
 * Req 2.4: wrap на границах с сохранением ориентации
 */
export class SquareTorusNavigator extends SquareGridNavigator {
  move(pos: Position, dir: Direction): MoveResult {
    const offset = this.getOffset(dir);
    let newX = pos.x + offset.x;
    let newY = pos.y + offset.y;

    // Тороидальное заворачивание - сохраняем ориентацию
    if (newX < 0) newX = this.width - 1;
    else if (newX >= this.width) newX = 0;
    
    if (newY < 0) newY = this.height - 1;
    else if (newY >= this.height) newY = 0;

    return {
      position: { x: newX, y: newY },
      direction: dir  // Ориентация сохраняется
    };
  }
}

/**
 * Навигатор для квадратной сетки с топологией бутылки Клейна
 * Req 2.5: wrap с инверсией ориентации на одной оси (горизонтальной)
 */
export class SquareKleinNavigator extends SquareGridNavigator {
  move(pos: Position, dir: Direction): MoveResult {
    const offset = this.getOffset(dir);
    let newX = pos.x + offset.x;
    let newY = pos.y + offset.y;
    let newDir = dir;

    // Вертикальное заворачивание - как тор
    if (newY < 0) newY = this.height - 1;
    else if (newY >= this.height) newY = 0;

    // Горизонтальное заворачивание - с инверсией
    if (newX < 0) {
      newX = this.width - 1;
      newY = this.height - 1 - newY;  // Отражение по Y
      newDir = this.mirrorDirectionVertical(dir);
    } else if (newX >= this.width) {
      newX = 0;
      newY = this.height - 1 - newY;  // Отражение по Y
      newDir = this.mirrorDirectionVertical(dir);
    }

    return {
      position: { x: newX, y: newY },
      direction: newDir
    };
  }

  /**
   * Отразить направление по вертикальной оси
   * 0 (вверх) <-> 2 (вниз), 1 и 3 остаются
   */
  private mirrorDirectionVertical(dir: Direction): Direction {
    if (dir === 0) return 2;
    if (dir === 2) return 0;
    return dir;
  }
}

/**
 * Навигатор для квадратной сетки с топологией проективной плоскости
 * Req 2.6: wrap с инверсией ориентации на обеих осях
 */
export class SquareProjectiveNavigator extends SquareGridNavigator {
  move(pos: Position, dir: Direction): MoveResult {
    const offset = this.getOffset(dir);
    let newX = pos.x + offset.x;
    let newY = pos.y + offset.y;
    let newDir = dir;

    // Горизонтальное заворачивание - с инверсией по Y
    if (newX < 0) {
      newX = this.width - 1;
      newY = this.height - 1 - newY;
      newDir = this.mirrorDirectionVertical(newDir);
    } else if (newX >= this.width) {
      newX = 0;
      newY = this.height - 1 - newY;
      newDir = this.mirrorDirectionVertical(newDir);
    }

    // Вертикальное заворачивание - с инверсией по X
    if (newY < 0) {
      newY = this.height - 1;
      newX = this.width - 1 - newX;
      newDir = this.mirrorDirectionHorizontal(newDir);
    } else if (newY >= this.height) {
      newY = 0;
      newX = this.width - 1 - newX;
      newDir = this.mirrorDirectionHorizontal(newDir);
    }

    return {
      position: { x: newX, y: newY },
      direction: newDir
    };
  }

  private mirrorDirectionVertical(dir: Direction): Direction {
    if (dir === 0) return 2;
    if (dir === 2) return 0;
    return dir;
  }

  private mirrorDirectionHorizontal(dir: Direction): Direction {
    if (dir === 1) return 3;
    if (dir === 3) return 1;
    return dir;
  }
}

/**
 * Навигатор для квадратной сетки с топологией сферы
 * Req 2.7: wrap по правилам сферической идентификации
 */
export class SquareSphereNavigator extends SquareGridNavigator {
  move(pos: Position, dir: Direction): MoveResult {
    const offset = this.getOffset(dir);
    let newX = pos.x + offset.x;
    let newY = pos.y + offset.y;
    let newDir = dir;

    // Верхний полюс (y < 0)
    if (newY < 0) {
      // Переход через северный полюс - разворот на 180°
      newY = 0;
      newX = this.width - 1 - newX;
      newDir = (dir + 2) % 4;  // Разворот
    }
    // Нижний полюс (y >= height)
    else if (newY >= this.height) {
      // Переход через южный полюс - разворот на 180°
      newY = this.height - 1;
      newX = this.width - 1 - newX;
      newDir = (dir + 2) % 4;  // Разворот
    }

    // Горизонтальное заворачивание - как тор (экватор)
    if (newX < 0) newX = this.width - 1;
    else if (newX >= this.width) newX = 0;

    return {
      position: { x: newX, y: newY },
      direction: newDir
    };
  }
}

/**
 * Навигатор для треугольной сетки с топологией тора
 * Req 2.8: 3 направления
 * Req 2.11: только тор топология
 * 
 * Треугольная сетка имеет чередующиеся up и down треугольники.
 * 
 * Система направлений:
 * Направление указывает на ГРАНЬ, через которую муравей будет двигаться.
 * - Up triangle: 0=левая грань, 1=правая грань, 2=нижняя грань
 * - Down triangle: 0=левая грань, 1=правая грань, 2=верхняя грань
 * 
 * Относительный поворот:
 * После перехода через грань муравей оказывается "лицом" к вершине,
 * противоположной грани входа. Поворот влево/вправо - это поворот
 * от этой вершины к соседней грани.
 * 
 * Для реализации относительного поворота:
 * - turnAmount=1 (вправо): следующая грань по часовой стрелке
 * - turnAmount=2 (влево): следующая грань против часовой стрелки
 * 
 * При переходе через грань направление трансформируется так,
 * чтобы сохранить относительную ориентацию муравья.
 */
export class TriangularTorusNavigator implements IGridNavigator {
  private readonly width: number;
  private readonly height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  getDirectionCount(): number {
    return 3;
  }

  /**
   * Поворот муравья на треугольной сетке.
   * 
   * ВАЖНО: В треугольной сетке муравей после шага "смотрит" на УГОЛ,
   * противоположный грани, через которую он вошёл. Поворот L/R/B - это
   * поворот от этого угла к одной из граней.
   * 
   * Интерпретация turnAmount (стандартная нотация LRB):
   * - 0 = L (влево) - поворот против часовой стрелки от угла к грани
   * - 1 = R (вправо) - поворот по часовой стрелке от угла к грани
   * - 2 = B (назад) - поворот к грани, противоположной углу (разворот)
   * 
   * currentDir указывает на угол, на который смотрит муравей.
   * Результат - грань, через которую муравей пойдёт.
   * 
   * Для up-треугольника (углы: 0=правый нижний, 1=левый нижний, 2=верхний):
   *   От угла 0: L→грань 2 (низ), R→грань 1 (правая), B→грань 0 (левая)
   *   От угла 1: L→грань 0 (левая), R→грань 2 (низ), B→грань 1 (правая)
   *   От угла 2: L→грань 1 (правая), R→грань 0 (левая), B→грань 2 (низ)
   * 
   * Для down-треугольника (углы: 0=правый верхний, 1=левый верхний, 2=нижний):
   *   От угла 0: L→грань 1 (правая), R→грань 2 (верх), B→грань 0 (левая)
   *   От угла 1: L→грань 2 (верх), R→грань 0 (левая), B→грань 1 (правая)
   *   От угла 2: L→грань 0 (левая), R→грань 1 (правая), B→грань 2 (верх)
   */
  turn(currentDir: Direction, turnAmount: Direction, pos?: Position): Direction {
    // currentDir = угол, на который смотрит муравей
    // результат = грань, через которую пойдёт
    
    const isUp = pos ? this.isUpTriangle(pos.x, pos.y) : true;
    
    // turnAmount: 0=L(влево), 1=R(вправо), 2=B(назад)
    if (turnAmount === 2) {
      // B (назад) - грань противоположная углу = грань с тем же индексом
      return currentDir;
    }
    
    if (isUp) {
      // Up-треугольник
      // Угол 0 (правый нижний): L→2, R→1
      // Угол 1 (левый нижний): L→0, R→2
      // Угол 2 (верхний): L→1, R→0
      const leftTurn = [2, 0, 1];   // от угла i поворот влево даёт грань leftTurn[i]
      const rightTurn = [1, 2, 0];  // от угла i поворот вправо даёт грань rightTurn[i]
      
      return turnAmount === 0 ? leftTurn[currentDir] : rightTurn[currentDir];
    } else {
      // Down-треугольник
      // Угол 0 (правый верхний): L→1, R→2
      // Угол 1 (левый верхний): L→2, R→0
      // Угол 2 (нижний): L→0, R→1
      const leftTurn = [1, 2, 0];
      const rightTurn = [2, 0, 1];
      
      return turnAmount === 0 ? leftTurn[currentDir] : rightTurn[currentDir];
    }
  }

  /**
   * Определить, является ли треугольник "вверх" или "вниз"
   * Чередование: (x + y) % 2 === 0 -> вверх, иначе вниз
   */
  private isUpTriangle(x: number, y: number): boolean {
    return (x + y) % 2 === 0;
  }

  /**
   * Трансформировать направление при переходе между треугольниками.
   * 
   * После перехода через грань муравей должен "смотреть" на грань,
   * через которую он пойдёт при следующем повороте L.
   * 
   * Внутренняя логика:
   * 1. Определяем грань входа в новый треугольник
   * 2. Угол, противоположный грани входа = грань входа (по индексу)
   * 3. Возвращаем грань, которая будет при повороте L от этого угла
   *    (это "направление по умолчанию" для визуализации)
   * 
   * Но для правильной работы turn() нам нужно хранить угол, а не грань.
   * Поэтому возвращаем угол (= индекс грани входа).
   */
  private transformDirection(exitDir: Direction, _fromUp: boolean, _toUp: boolean): Direction {
    // Определяем грань входа в новый треугольник
    let entryEdge: Direction;
    if (exitDir === 0) entryEdge = 1;      // вышел через левую → вошёл через правую
    else if (exitDir === 1) entryEdge = 0; // вышел через правую → вошёл через левую
    else entryEdge = 2;                     // вышел через верх/низ → вошёл через низ/верх
    
    // Муравей смотрит на угол, противоположный грани входа
    // Угол i противоположен грани i, поэтому угол = entryEdge
    return entryEdge;
  }
  
  /**
   * Преобразовать внутреннее направление (угол) в грань для визуализации.
   * Возвращает грань, на которую муравей "смотрит" визуально.
   * Используется рендерером для отображения направления муравья.
   * 
   * По умолчанию показываем грань, которая будет при повороте L.
   */
  getVisualDirection(currentDir: Direction, x: number, y: number): Direction {
    const isUp = this.isUpTriangle(x, y);
    
    if (isUp) {
      // Up-треугольник: от угла i поворот L даёт грань
      const leftTurn = [2, 0, 1];
      return leftTurn[currentDir];
    } else {
      // Down-треугольник
      const leftTurn = [1, 2, 0];
      return leftTurn[currentDir];
    }
  }

  move(pos: Position, dir: Direction): MoveResult {
    const isUp = this.isUpTriangle(pos.x, pos.y);
    let newX = pos.x;
    let newY = pos.y;

    // Направления для треугольников:
    // Up triangle: 0=left (x-1), 1=right (x+1), 2=down (y+1)
    // Down triangle: 0=left (x-1), 1=right (x+1), 2=up (y-1)
    if (isUp) {
      switch (dir) {
        case 0: // левая грань
          newX = pos.x - 1;
          break;
        case 1: // правая грань
          newX = pos.x + 1;
          break;
        case 2: // нижняя грань
          newY = pos.y + 1;
          break;
      }
    } else {
      switch (dir) {
        case 0: // левая грань
          newX = pos.x - 1;
          break;
        case 1: // правая грань
          newX = pos.x + 1;
          break;
        case 2: // верхняя грань
          newY = pos.y - 1;
          break;
      }
    }

    // Тороидальное заворачивание
    if (newX < 0) newX = this.width - 1;
    else if (newX >= this.width) newX = 0;
    
    if (newY < 0) newY = this.height - 1;
    else if (newY >= this.height) newY = 0;

    // Определяем ориентацию нового треугольника
    const newIsUp = this.isUpTriangle(newX, newY);
    
    // Трансформируем направление - муравей теперь смотрит на грань,
    // через которую он вошёл
    const newDir = this.transformDirection(dir, isUp, newIsUp);

    return {
      position: { x: newX, y: newY },
      direction: newDir
    };
  }
}

/**
 * Навигатор для шестиугольной сетки с топологией тора
 * Req 2.10: 6 направлений
 * Req 2.11: только тор топология
 * 
 * Шестиугольная сетка с относительной ориентацией муравья.
 * 
 * Система направлений (pointy-top hexagon):
 * Направления указывают на ГРАНИ: 0=E, 1=SE, 2=SW, 3=W, 4=NW, 5=NE
 * 
 * Относительный поворот:
 * После перехода через грань муравей сохраняет свою ориентацию
 * относительно мира. Поворот выполняется относительно текущего "вперёд".
 * 
 * Интерпретация turnAmount (стандартная нотация LFRSPB):
 * - 0 = L (Left) - влево на 1 грань
 * - 1 = F (Forward) - вперёд, без поворота
 * - 2 = R (Right) - вправо на 1 грань
 * - 3 = S (Starboard) - вправо на 2 грани
 * - 4 = P (Port) - влево на 2 грани
 * - 5 = B (Back) - назад, разворот на 180°
 * 
 * При 6 поворотах влево (LLLLLL = 0,0,0,0,0,0) муравей описывает шестиугольник.
 */
export class HexagonalTorusNavigator implements IGridNavigator {
  private readonly width: number;
  private readonly height: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  getDirectionCount(): number {
    return 6;
  }

  /**
   * Поворот муравья на шестиугольной сетке.
   * 
   * currentDir - грань, на которую "смотрит" муравей (его "вперёд")
   * turnAmount - тип поворота (стандартная нотация LFRSPB):
   *   0 = L (Left) - влево на 1 грань (-1)
   *   1 = F (Forward) - вперёд, без поворота (0)
   *   2 = R (Right) - вправо на 1 грань (+1)
   *   3 = S (Starboard) - вправо на 2 грани (+2)
   *   4 = P (Port) - влево на 2 грани (-2)
   *   5 = B (Back) - назад, разворот (+3)
   * 
   * Результат - новая грань, через которую муравей пойдёт.
   */
  turn(currentDir: Direction, turnAmount: Direction, _pos?: Position): Direction {
    // Маппинг turnAmount на смещение направления (LFRSPB)
    // 0=L(-1), 1=F(0), 2=R(+1), 3=S(+2), 4=P(-2), 5=B(+3)
    const turnOffsets = [-1, 0, 1, 2, -2, 3];
    const offset = turnOffsets[turnAmount % 6];
    
    return ((currentDir + offset) % 6 + 6) % 6;
  }

  /**
   * Получить смещение для шестиугольной сетки (offset coordinates)
   * Используем "odd-r" layout: нечётные ряды смещены вправо
   * Направления: 0=E, 1=SE, 2=SW, 3=W, 4=NW, 5=NE
   */
  private getOffset(dir: Direction, row: number): Position {
    const isOddRow = row % 2 === 1;
    
    // Смещения для чётных и нечётных рядов
    const evenOffsets: Position[] = [
      { x: 1, y: 0 },   // 0: E
      { x: 0, y: 1 },   // 1: SE
      { x: -1, y: 1 },  // 2: SW
      { x: -1, y: 0 },  // 3: W
      { x: -1, y: -1 }, // 4: NW
      { x: 0, y: -1 }   // 5: NE
    ];
    
    const oddOffsets: Position[] = [
      { x: 1, y: 0 },   // 0: E
      { x: 1, y: 1 },   // 1: SE
      { x: 0, y: 1 },   // 2: SW
      { x: -1, y: 0 },  // 3: W
      { x: 0, y: -1 },  // 4: NW
      { x: 1, y: -1 }   // 5: NE
    ];

    return isOddRow ? oddOffsets[dir] : evenOffsets[dir];
  }

  /**
   * Трансформировать направление при переходе между шестиугольниками.
   * 
   * После перехода через грань exitDir муравей входит через грань (exitDir + 3) % 6.
   * Его новое "вперёд" - грань, противоположная грани входа, т.е. exitDir.
   * 
   * Таким образом, направление сохраняется (муравей продолжает "смотреть" туда же).
   */
  private transformDirection(exitDir: Direction): Direction {
    // Муравей вышел через грань exitDir, вошёл через (exitDir + 3) % 6
    // Его "вперёд" теперь - противоположная грань входа = exitDir
    return exitDir;
  }

  move(pos: Position, dir: Direction): MoveResult {
    const offset = this.getOffset(dir, pos.y);
    let newX = pos.x + offset.x;
    let newY = pos.y + offset.y;

    // Тороидальное заворачивание
    if (newX < 0) newX = this.width - 1;
    else if (newX >= this.width) newX = 0;
    
    if (newY < 0) newY = this.height - 1;
    else if (newY >= this.height) newY = 0;

    // Трансформируем направление - муравей сохраняет ориентацию
    const newDir = this.transformDirection(dir);

    return {
      position: { x: newX, y: newY },
      direction: newDir
    };
  }
}

/**
 * Фабрика для создания навигаторов
 * Req 2.11: не-квадратные сетки поддерживают только тор
 */
export function createGridNavigator(
  gridType: GridType,
  topology: Topology,
  width: number,
  height: number
): IGridNavigator {
  // Проверка: не-квадратные сетки поддерживают только тор
  if (gridType !== GridType.Square && topology !== Topology.Torus) {
    throw new Error('Triangular and hexagonal grids only support torus topology');
  }

  switch (gridType) {
    case GridType.Square:
      switch (topology) {
        case Topology.Torus:
          return new SquareTorusNavigator(width, height);
        case Topology.KleinBottle:
          return new SquareKleinNavigator(width, height);
        case Topology.ProjectivePlane:
          return new SquareProjectiveNavigator(width, height);
        case Topology.Sphere:
          return new SquareSphereNavigator(width, height);
      }
      break;
    case GridType.Triangular:
      return new TriangularTorusNavigator(width, height);
    case GridType.Hexagonal:
      return new HexagonalTorusNavigator(width, height);
  }
}
